import { useState } from "react";
import cx from "classnames";
import { faArrowDownWideShort, faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import Icon from "common/components/Icon";

import PillButton from "client/common/components/buttons/PillButton";
import { ProductListSource } from "client/analytics/types";
import {
  getProductListIdentity,
  ProductListContext,
  useSendPostHogProductListSortChange,
} from "client/analytics/utils";

import { ProductListSort } from "../types";
import { getProductListSort } from "../utils";

import styles from "../styles/ProductListSortSelect.module.less";

const SORT_LABELS: Record<ProductListSort, string> = {
  [ProductListSort.Default]: "Featured",
  [ProductListSort.BestSellers]: "Best sellers",
  [ProductListSort.NameAsc]: "Name A-Z",
  [ProductListSort.NameDesc]: "Name Z-A",
};

type ProductListSortSelectProps = {
  value: ProductListSort;
  options: ProductListSort[];
  onChange: (sort: ProductListSort) => void;
  listSource: ProductListSource;
  listContext: ProductListContext;
};

/** A secondary pill over a native select, so phones get the platform picker. */
const ProductListSortSelect = ({
  value,
  options,
  onChange,
  listSource,
  listContext,
}: ProductListSortSelectProps) => {
  const sendSortChange = useSendPostHogProductListSortChange();
  // Desktop browsers keep focus on the select after a mouse pick, so the ring shows for keyboard
  // focus only.
  const [isPointerFocus, setIsPointerFocus] = useState(false);

  return (
    <div className={cx(styles.container, isPointerFocus && styles.pointerFocus)}>
      <span aria-hidden={true}>
        <PillButton variant="secondary" size="s" tabIndex={-1} className={styles.pill}>
          <span className={styles.label}>
            <Icon className={styles.icon} icon={faArrowDownWideShort} />
            <span className={styles.text}>
              Sort: <span className={styles.value}>{SORT_LABELS[value]}</span>
            </span>
            <Icon className={styles.chevron} icon={faChevronDown} />
          </span>
        </PillButton>
      </span>
      <select
        className={styles.select}
        value={value}
        onPointerDown={() => setIsPointerFocus(true)}
        onKeyDown={() => setIsPointerFocus(false)}
        onBlur={() => setIsPointerFocus(false)}
        onChange={(event) => {
          const next = getProductListSort(event.target.value);
          sendSortChange({
            source: listSource,
            sort: next,
            previous_sort: value,
            ...getProductListIdentity(listContext),
          });
          onChange(next);
        }}
        aria-label="Sort products"
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {SORT_LABELS[option]}
          </option>
        ))}
      </select>
    </div>
  );
};

export default ProductListSortSelect;
